import { type FileViewerComponents } from './FileViewer.components'
import { type FileViewerStrings } from './FileViewer.strings'
import { FileViewerContext, useFileViewerContext } from './FileViewerContext'
import { FileViewerStyled } from './styles/FileViewerStyled'
import { useFileViewer } from './useFileViewer'
import { useFileViewerState } from './useFileViewerState'
import { type IFileData } from '../File/File'

export interface IFileViewerProps<FileType = IFileData> {
  files: FileType[]
  selectedFile?: FileType
  defaultFirstFile?: boolean
  initialSelectedFile?: FileType
  onSelectedFileChange?: (file: FileType) => void
  getFileData?: (file: FileType) => IFileData
  isFileSelected?: (file: FileType, selectedFile: FileType) => boolean
  components?: Partial<FileViewerComponents<FileType>>
  strings?: Partial<FileViewerStrings>
}

const FileViewerComponent = () => {
  const { Components, selectedFile } = useFileViewerContext()

  return (
    <FileViewerStyled>
      <Components.PreviewSection>
        {selectedFile ? <Components.Preview /> : <Components.EmptyPreview />}
      </Components.PreviewSection>

      <Components.ControlsSection>
        <Components.FileList />
      </Components.ControlsSection>
    </FileViewerStyled>
  )
}

export const FileViewer = <FileType extends object = IFileData>({
  files,
  onSelectedFileChange: onSelectedFileChangeProp = () => undefined,
  selectedFile: selectedFileProp,
  defaultFirstFile = true,
  initialSelectedFile,
  getFileData,
  isFileSelected = (file, _selectedFile) => {
    return file === _selectedFile
  },
  components,
  strings,
}: IFileViewerProps<FileType>) => {
  const { selectedFile, onSelectedFileChange } = useFileViewerState<FileType>({
    files,
    defaultFirstFile,
    initialSelectedFile,
    onSelectedFileChangeProp,
    selectedFileProp,
    isFileSelected,
  })

  const { contextValues } = useFileViewer<FileType>({
    files,
    selectedFile,
    onSelectedFileChange,
    getFileData,
    isFileSelected,
    components,
    strings,
  })

  return (
    <FileViewerContext.Provider value={contextValues}>
      <FileViewerComponent />
    </FileViewerContext.Provider>
  )
}
